// A compact stored form for library items (TraceCAD 3.4.0).
//
// WHY: an Eden document body holds at most 100,000 characters, and localStorage about
// 5 MB for everything. A library item written as full entities repeats a style object
// (about 150 characters) on EVERY line, so a drawing of a few hundred shapes already
// overflowed. This form keeps styles once, drops ids/layers/flags that every library
// object resets anyway, and writes points as flat number lists rounded to 0.01 mm.
//
// Items saved before 3.4.0 are the long form (no `v`). decodeItem reads both.
import { newId } from "../doc/document";
import type { Entity, Style, TextStyle } from "../doc/entities";
import { isLibraryItem, type LibraryItem } from "./library";
import type { Units } from "../doc/entities";

type Num = number;
export type CompactShape =
  | ["p", Num, 0 | 1, Num[]] // polyline: style, closed, x0,y0,x1,y1…
  | ["c", Num, Num, Num, Num] // circle: style, x, y, r
  | ["a", Num, Num, Num, Num, Num, Num] // arc: style, x, y, r, startDeg, sweepDeg
  | ["t", Num, Num, Num, Num, string, Num?] // text: textStyle, x, y, rotationDeg, content, boxWidth
  | ["g", Num[]] // group: indexes of its children (always earlier in the list)
  | ["x", Record<string, unknown>]; // anything else, whole (ids kept)

export interface CompactItem {
  v: 2;
  id: string;
  name: string;
  category: string;
  units: Units;
  updatedAt: string;
  size: { w: number; h: number };
  styles: Style[];
  tstyles: TextStyle[];
  shapes: CompactShape[];
}

const r2 = (n: number): number => Math.round(n * 100) / 100;
const r3 = (n: number): number => Math.round(n * 1000) / 1000;

/** Index of `value` in `list`, adding it when it is new (compared by content). */
function intern<T>(list: T[], seen: Map<string, number>, value: T): number {
  const key = JSON.stringify(value);
  const at = seen.get(key);
  if (at !== undefined) return at;
  list.push(value);
  seen.set(key, list.length - 1);
  return list.length - 1;
}

const plain = (e: Entity): boolean => !e.locked && !e.hidden && !("meta" in e && e.meta);

export function encodeItem(item: LibraryItem): CompactItem {
  const styles: Style[] = [];
  const tstyles: TextStyle[] = [];
  const sSeen = new Map<string, number>();
  const tSeen = new Map<string, number>();
  const shapes: CompactShape[] = [];
  const index = new Map<string, number>();

  // Children before their group: a group's children are indexes into what is already written.
  const written = new Set<string>();
  const byId = new Map(item.entities.map((e) => [e.id, e]));
  const write = (e: Entity): void => {
    if (written.has(e.id)) return;
    if (e.type === "group") for (const c of e.children) {
      const child = byId.get(c);
      if (child) write(child);
    }
    written.add(e.id);
    let shape: CompactShape;
    if (e.type === "polyline" && plain(e)) {
      shape = ["p", intern(styles, sSeen, e.style), e.closed ? 1 : 0, e.points.flatMap((p) => [r2(p.x), r2(p.y)])];
    } else if (e.type === "circle" && plain(e)) {
      shape = ["c", intern(styles, sSeen, e.style), r2(e.center.x), r2(e.center.y), r2(e.r)];
    } else if (e.type === "arc" && plain(e)) {
      shape = ["a", intern(styles, sSeen, e.style), r2(e.center.x), r2(e.center.y), r2(e.r), r3(e.startDeg), r3(e.sweepDeg)];
    } else if (e.type === "text" && plain(e)) {
      shape = ["t", intern(tstyles, tSeen, e.textStyle), r2(e.pos.x), r2(e.pos.y), r3(e.rotationDeg), e.content, ...(e.boxWidth !== undefined ? [r2(e.boxWidth)] : [])] as CompactShape;
    } else if (e.type === "group" && plain(e)) {
      shape = ["g", e.children.map((c) => index.get(c)).filter((i): i is number => i !== undefined)];
    } else {
      shape = ["x", structuredClone(e) as unknown as Record<string, unknown>];
    }
    index.set(e.id, shapes.length);
    shapes.push(shape);
  };
  for (const e of item.entities) write(e);

  return {
    v: 2,
    id: item.id,
    name: item.name,
    category: item.category,
    units: item.units,
    updatedAt: item.updatedAt,
    size: { w: r2(item.size.w), h: r2(item.size.h) },
    styles,
    tstyles,
    shapes
  };
}

const base = { layerId: "", locked: false, hidden: false } as const;

/** Read an item in either form. Null when it is neither. */
export function decodeItem(raw: unknown): LibraryItem | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 2) return isLibraryItem(raw) ? raw : null; // the long form, saved before 3.4.0

  const c = raw as CompactItem;
  if (!Array.isArray(c.shapes) || !Array.isArray(c.styles) || !Array.isArray(c.tstyles) || c.shapes.length === 0) return null;
  const ids: string[] = [];
  const entities: Entity[] = [];
  for (const s of c.shapes) {
    const id = s[0] === "x" ? String((s[1] as { id?: unknown }).id ?? newId()) : newId();
    ids.push(id);
    switch (s[0]) {
      case "p": {
        const pts = [];
        for (let i = 0; i + 1 < s[3].length; i += 2) pts.push({ x: s[3][i]!, y: s[3][i + 1]! });
        const style = c.styles[s[1]];
        if (!style) return null;
        entities.push({ ...base, id, type: "polyline", points: pts, closed: s[2] === 1, style: structuredClone(style) });
        break;
      }
      case "c": {
        const style = c.styles[s[1]];
        if (!style) return null;
        entities.push({ ...base, id, type: "circle", center: { x: s[2], y: s[3] }, r: s[4], style: structuredClone(style) });
        break;
      }
      case "a": {
        const style = c.styles[s[1]];
        if (!style) return null;
        entities.push({ ...base, id, type: "arc", center: { x: s[2], y: s[3] }, r: s[4], startDeg: s[5], sweepDeg: s[6], style: structuredClone(style) });
        break;
      }
      case "t": {
        const ts = c.tstyles[s[1]];
        if (!ts) return null;
        entities.push({ ...base, id, type: "text", pos: { x: s[2], y: s[3] }, rotationDeg: s[4], content: s[5], textStyle: structuredClone(ts), ...(s[6] !== undefined ? { boxWidth: s[6] } : {}) });
        break;
      }
      case "g":
        entities.push({ ...base, id, type: "group", children: s[1].map((i) => ids[i]).filter((x): x is string => !!x) });
        break;
      default:
        entities.push({ ...(structuredClone(s[1]) as unknown as Entity), id });
    }
  }
  const item: LibraryItem = {
    id: c.id,
    name: c.name,
    category: c.category,
    entities,
    size: c.size,
    units: c.units,
    updatedAt: c.updatedAt
  };
  return isLibraryItem(item) ? item : null;
}

/** The text that is stored. Compact JSON, no spaces. */
export const itemJson = (item: LibraryItem): string => JSON.stringify(encodeItem(item));
